import { SearchNormal1 } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Patient } from '@/types/patient';
import type { ChangeEvent, ReactNode } from 'react';

export interface AssignPatientListItem {
    patient: Patient;
    isSelectable?: boolean;
    badge?: ReactNode;
    helperText?: string;
}

interface AssignPatientSelectStepProps {
    patients: AssignPatientListItem[];
    isLoading?: boolean;
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onSelectPatient: (patient: Patient) => void;
}

export default function AssignPatientSelectStep({
    patients,
    isLoading = false,
    searchQuery,
    onSearchChange,
    onSelectPatient,
}: AssignPatientSelectStepProps) {
    return (
        <div className="space-y-4">
            <div className="relative">
                <SearchNormal1 className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Buscar paciente..."
                    value={searchQuery}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onSearchChange(event.target.value)
                    }
                    className="pl-10"
                />
            </div>
            <div className="max-h-[50vh] space-y-2 overflow-y-auto sm:max-h-[300px]">
                {isLoading ? (
                    <p className="py-4 text-center text-muted-foreground">
                        Carregando pacientes...
                    </p>
                ) : patients.length === 0 ? (
                    <p className="py-4 text-center text-muted-foreground">
                        Nenhum paciente encontrado.
                    </p>
                ) : (
                    patients.map((item) => {
                        const { patient, isSelectable = true, badge, helperText } = item;

                        return (
                            <button
                                key={patient.id}
                                type="button"
                                onClick={() => isSelectable && onSelectPatient(patient)}
                                disabled={!isSelectable}
                                className={cn(
                                    'flex min-h-14 w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    isSelectable
                                        ? 'cursor-pointer border-transparent hover:bg-muted'
                                        : 'cursor-not-allowed border-border bg-surface/80 opacity-80',
                                )}
                            >
                                <PatientAvatar
                                    name={patient.full_name}
                                    avatarUrl={patient.avatar_url}
                                    className="size-8"
                                    fallbackClassName="text-xs"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-foreground">
                                        {patient.full_name}
                                    </p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {patient.email || 'Sem email cadastrado'}
                                    </p>
                                    {helperText && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {helperText}
                                        </p>
                                    )}
                                </div>
                                {badge && <div className="ml-2 shrink-0">{badge}</div>}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
